import React, { useState } from 'react';
import { Row, Col } from 'react-bootstrap';
import { formatLocalFullDateLabel } from '../../../utils/dateUtils';
import { huntService } from '../services/huntService';
import { YDialog } from '../../../ui/dialog/YDialog';
import { YButton } from '../../../ui/YButton';
import { YCard } from '../../../ui/YCard';
import { HuntSectionHeader } from './HuntSectionHeader';
import Lightbox from '../../../components/common/Lightbox';
import { CachedImage } from '../../../components/common/CachedImage';
import {
  formatHuntWeatherSummary,
  getHuntWeatherPeriods,
} from '../utils/huntWeatherDisplay';
import '../../../ui/dialog/yDialog.css';
import './hunts-shell.css';

function formatPeriodLabel(period) {
  return period.charAt(0).toUpperCase() + period.slice(1).replace('_', ' ');
}

function HuntDetailField({ label, children }) {
  return (
    <div>
      <span className="hunts-detail__field-label">{label}</span>
      <p className="hunts-detail__field-value">{children}</p>
    </div>
  );
}

function formatWeatherValue(value, suffix = '') {
  if (value == null || value === '') return '—';
  return `${value}${suffix}`;
}

const HuntDetailModal = ({ show, onHide, hunt }) => {
  const [showLightbox, setShowLightbox] = useState(false);
  if (!hunt) return null;

  const formatDate = (dateString) =>
    formatLocalFullDateLabel(dateString).replace(/^(Sun|Mon|Tue|Wed|Thu|Fri|Sat),?\s*/, '');

  const getTimePeriodsDisplay = (periods) => {
    if (!periods || periods.length === 0) return 'No periods specified';
    return periods.map((period) => formatPeriodLabel(period)).join(', ');
  };

  const weatherPeriods = getHuntWeatherPeriods(hunt.weather_data);

  const handleReplacePhoto = async (file) => {
    if (!file || !hunt?.harvest_details?.id) return;
    try {
      const img = await (async () => {
        const image = new window.Image();
        const objectUrl = URL.createObjectURL(file);
        await new Promise((res, rej) => {
          image.onload = res;
          image.onerror = rej;
          image.src = objectUrl;
        });
        const canvas = document.createElement('canvas');
        let { width, height } = image;
        const ratio = width / height;
        if (width > height) {
          if (width > 1600) {
            height = Math.round(1600 / ratio);
            width = 1600;
          }
        } else if (height > 1600) {
          width = Math.round(1600 * ratio);
          height = 1600;
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(image, 0, 0, width, height);
        const mainBlob = await new Promise((res) => canvas.toBlob(res, 'image/jpeg', 0.8));
        const thumbCanvas = document.createElement('canvas');
        let tw = image.width;
        let th = image.height;
        const tr = tw / th;
        if (tw > th) {
          if (tw > 320) {
            th = Math.round(320 / tr);
            tw = 320;
          }
        } else if (th > 320) {
          tw = Math.round(320 * tr);
          th = 320;
        }
        thumbCanvas.width = tw;
        thumbCanvas.height = th;
        const tctx = thumbCanvas.getContext('2d');
        tctx.drawImage(image, 0, 0, tw, th);
        const thumbBlob = await new Promise((res) => thumbCanvas.toBlob(res, 'image/jpeg', 0.7));
        URL.revokeObjectURL(objectUrl);
        return { mainBlob, thumbBlob, width, height };
      })();
      const init = await huntService.initHarvestPhotoUpload(
        hunt.harvest_details.id,
        file.name,
        file.type || 'image/jpeg'
      );
      await fetch(init.upload_url, {
        method: 'PUT',
        body: img.mainBlob,
        headers: { 'Content-Type': file.type || 'image/jpeg' },
      });
      await fetch(init.thumb_upload_url, {
        method: 'PUT',
        body: img.thumbBlob,
        headers: { 'Content-Type': file.type || 'image/jpeg' },
      });
      await huntService.attachHarvestPhoto(hunt.harvest_details.id, {
        object_key: init.object_key,
        thumb_object_key: init.thumb_object_key,
        caption: hunt.harvest_details.photo_caption || '',
        width: img.width,
        height: img.height,
        size_bytes: img.mainBlob.size,
      });
      onHide?.();
    } catch (err) {
      console.error('Replace photo failed', err);
    }
  };

  const totalDeer =
    hunt.total_deer_seen ??
    (Number(hunt.does_seen) || 0) + (Number(hunt.bucks_seen) || 0) + (Number(hunt.fawns_seen) || 0);

  return (
    <>
      <YDialog
        show={show}
        onHide={onHide}
        size="lg"
        title="Hunt details"
        dialogClassName="hunts-detail-dialog"
        testId="hunt-detail-dialog"
        footer={<YButton variant="secondary" onClick={onHide}>Close</YButton>}
      >
        <section className="hunts-detail__section">
          <HuntSectionHeader label="Overview" />
          <div className="hunts-detail__grid">
            <HuntDetailField label="Date">{formatDate(hunt.date)}</HuntDetailField>
            <HuntDetailField label="Location">{hunt.location}</HuntDetailField>
            <HuntDetailField label="Time periods">
              {getTimePeriodsDisplay(hunt.time_periods)}
            </HuntDetailField>
            <HuntDetailField label="Weather summary">
              {formatHuntWeatherSummary(hunt.weather_data)}
            </HuntDetailField>
          </div>
        </section>

        <section className="hunts-detail__section">
          <HuntSectionHeader label="Sightings" title="Deer observed" />
          <div className="hunts-detail__deer-row">
            <div className="hunts-detail__deer-item">
              <strong>Does</strong>
              <span>{hunt.does_seen}</span>
            </div>
            <div className="hunts-detail__deer-item">
              <strong>Bucks</strong>
              <span>{hunt.bucks_seen}</span>
            </div>
            <div className="hunts-detail__deer-item">
              <strong>Fawns</strong>
              <span>{hunt.fawns_seen}</span>
            </div>
            <div className="hunts-detail__deer-item">
              <strong>Total</strong>
              <span>{totalDeer}</span>
            </div>
          </div>
        </section>

        <section className="hunts-detail__section">
          <HuntSectionHeader label="Outcomes" />
          <div className="hunts-shell__chip-row">
            {hunt.shot_taken ? (
              <span className="hunts-shell__chip hunts-shell__chip--shot">Shot taken</span>
            ) : null}
            {hunt.harvest ? (
              <span className="hunts-shell__chip hunts-shell__chip--harvest">Harvest</span>
            ) : null}
            {!hunt.shot_taken && !hunt.harvest ? (
              <span className="hunts-shell__chip">No action</span>
            ) : null}
          </div>
        </section>

        {weatherPeriods.length > 0 ? (
          <section className="hunts-detail__section">
            <YCard
              headerVariant="muted"
              header={<span className="hunts-shell__panel-title">Weather conditions</span>}
            >
              <Row className="g-3">
                {weatherPeriods.map(({ period, label, data }) => (
                  <Col md={6} key={period}>
                    <div className="hunts-detail__weather-period">
                      <div className="hunts-detail__weather-period-title">{label}</div>
                      <div className="hunts-detail__meta">
                        <div>Temperature: {formatWeatherValue(data.temperature, '°F')}</div>
                        <div>
                          Wind:{' '}
                          {data.wind_speed != null
                            ? `${data.wind_speed} mph${
                                data.wind_direction_text ? ` ${data.wind_direction_text}` : ''
                              }`
                            : '—'}
                        </div>
                        <div>Humidity: {formatWeatherValue(data.humidity, '%')}</div>
                        <div>Conditions: {formatWeatherValue(data.description)}</div>
                      </div>
                    </div>
                  </Col>
                ))}
              </Row>
            </YCard>
          </section>
        ) : null}

        {hunt.harvest && hunt.harvest_details ? (
          <section className="hunts-detail__section">
            <YCard headerVariant="muted" header={<span className="hunts-shell__panel-title">Harvest details</span>}>
              <div className="hunts-detail__grid">
                <HuntDetailField label="Deer type">
                  {hunt.harvest_details.deer_type.charAt(0).toUpperCase() +
                    hunt.harvest_details.deer_type.slice(1)}
                </HuntDetailField>
                <HuntDetailField label="Method">
                  {hunt.harvest_details.method.charAt(0).toUpperCase() +
                    hunt.harvest_details.method.slice(1)}
                </HuntDetailField>
                {hunt.harvest_details.weight ? (
                  <HuntDetailField label="Weight">{hunt.harvest_details.weight} lbs</HuntDetailField>
                ) : null}
                {hunt.harvest_details.deer_type === 'buck' && hunt.harvest_details.points ? (
                  <HuntDetailField label="Points">{hunt.harvest_details.points}</HuntDetailField>
                ) : null}
              </div>
              {hunt.harvest_details.notable_features ? (
                <div className="mt-3">
                  <HuntDetailField label="Notable features">
                    {hunt.harvest_details.notable_features}
                  </HuntDetailField>
                </div>
              ) : null}
              {hunt.harvest_details.photo_thumb_url || hunt.harvest_details.photo_url ? (
                <div className="mt-3">
                  <span className="hunts-detail__field-label">Harvest photo</span>
                  <CachedImage
                    src={hunt.harvest_details.photo_thumb_url || hunt.harvest_details.photo_url}
                    alt="Harvest"
                    className="hunts-detail__harvest-photo mt-2"
                    onClick={() => setShowLightbox(true)}
                  />
                  {hunt.harvest_details.photo_caption ? (
                    <p className="hunts-detail__meta mt-1">{hunt.harvest_details.photo_caption}</p>
                  ) : null}
                  {hunt?.id && hunt?.harvest_details ? (
                    <div className="hunts-detail__photo-actions">
                      <label className="y-btn y-btn-secondary y-btn-sm mb-0" style={{ cursor: 'pointer' }}>
                        Change photo
                        <input
                          type="file"
                          accept="image/*"
                          hidden
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            handleReplacePhoto(file);
                            e.target.value = '';
                          }}
                        />
                      </label>
                      <YButton
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={async () => {
                          if (!window.confirm('Remove harvest photo?')) return;
                          try {
                            await huntService.deleteHarvestPhoto(hunt.harvest_details.id);
                            onHide?.();
                          } catch (err) {
                            console.error('Delete photo failed', err);
                          }
                        }}
                      >
                        Remove photo
                      </YButton>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </YCard>
          </section>
        ) : null}

        {hunt.notes ? (
          <section className="hunts-detail__section">
            <HuntSectionHeader label="Notes" />
            <p className="hunts-detail__field-value">{hunt.notes}</p>
          </section>
        ) : null}

        <p className="hunts-detail__meta mb-0">
          Created {new Date(hunt.created_at).toLocaleString()}
          {hunt.updated_at !== hunt.created_at
            ? ` · Updated ${new Date(hunt.updated_at).toLocaleString()}`
            : ''}
        </p>
      </YDialog>

      {showLightbox && hunt.harvest_details ? (
        <Lightbox
          items={[
            {
              url: hunt.harvest_details.photo_url,
              getUrl: async () => hunt.harvest_details.photo_url,
              caption: hunt.harvest_details.photo_caption || '',
            },
          ]}
          initialIndex={0}
          onClose={() => setShowLightbox(false)}
        />
      ) : null}
    </>
  );
};

export default HuntDetailModal;
